/* DvumPanel — Deal Volume Under Management (Ultra Admin).
 * La métrique investisseur : volume de deals piloté dans Noam, commissions
 * potentielles, événements facturables (NDA, data rooms), croissance mensuelle.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, TrendingUp, RefreshCw, Landmark, ShieldCheck, Link2, FolderOpen } from "lucide-react";

function chf(n) {
  if (n == null) return "—";
  if (n >= 1e9) return `CHF ${(n / 1e9).toFixed(2)} Mrd`;
  if (n >= 1e6) return `CHF ${(n / 1e6).toFixed(1)} M`;
  if (n >= 1e3) return `CHF ${Math.round(n / 1e3)} k`;
  return `CHF ${Math.round(n)}`;
}

function Kpi({ label, value, sub, accent = "#EFD127", icon: Icon }) {
  return (
    <div className="noam-card p-4" data-testid={`dvum-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />} {label}
      </div>
      <div className="noam-display text-2xl font-bold mt-2" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function DvumPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ultra-admin/dvum");
      setData(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Agrégation cross-org…</div>;
  if (!data) return null;
  const t = data.totals;
  const maxVol = Math.max(1, ...(data.monthly || []).map((m) => m.volume_chf));

  return (
    <div className="space-y-5" data-testid="dvum-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="noam-overline">La métrique investisseur</div>
          <h2 className="noam-display text-xl font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#EFD127]" /> Deal Volume Under Management
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Valeur transactionnelle vivant dans Noam, toutes organisations confondues — l'équivalent de l'AuM d'Avaloq.
          </p>
        </div>
        <button onClick={load} className="btn-secondary text-xs" data-testid="dvum-refresh"><RefreshCw className="w-3.5 h-3.5" /> Actualiser</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="DVUM (mandats actifs)" value={chf(t.volume_chf)} sub={`${t.mandates_active} mandat(s) actif(s) · ${t.mandates_total} au total`} icon={Landmark} />
        <Kpi label="Commissions potentielles" value={chf(t.commission_chf)} sub="pipeline pondéré au taux de chaque mandat" accent="#1D9E75" icon={TrendingUp} />
        <Kpi label="Événements facturables" value={t.billable_events} sub={`${t.ndas_signed} NDA signé(s) · ${t.shares} lien(s) de partage`} accent="#378ADD" icon={ShieldCheck} />
        <Kpi label="Data rooms" value={t.dataroom_docs} sub={`documents en data room · ${t.orgs_with_deals} org(s) avec deals`} accent="#EF9F27" icon={FolderOpen} />
      </div>

      {/* Croissance mensuelle */}
      {(data.monthly || []).length > 0 && (
        <div className="noam-card p-4" data-testid="dvum-monthly">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Volume de deals entrés — 6 derniers mois</div>
          <div className="flex items-end gap-3 h-36">
            {data.monthly.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                <div className="text-[10px] text-[#EFD127] font-mono">{m.volume_chf ? chf(m.volume_chf) : ""}</div>
                <div className="w-full rounded-t bg-gradient-to-t from-[#EFD127]/20 to-[#EFD127]/60 border-t border-[#EFD127]"
                  style={{ height: `${Math.max(3, (m.volume_chf / maxVol) * 100)}%` }} />
                <div className="text-[9px] text-zinc-500">{m.month}</div>
                <div className="text-[9px] text-zinc-600">{m.mandates} mdt · {m.ndas} NDA · {m.shares} <Link2 className="w-2.5 h-2.5 inline" /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Par organisation */}
      <div className="noam-card p-0 overflow-x-auto" data-testid="dvum-per-org">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <th className="px-3 py-2.5">Organisation</th>
              <th className="px-3 py-2.5 text-right">DVUM actif</th>
              <th className="px-3 py-2.5 text-right">Commissions pot.</th>
              <th className="px-3 py-2.5 text-right">Mandats</th>
              <th className="px-3 py-2.5 text-right">NDA signés</th>
              <th className="px-3 py-2.5 text-right">Partages</th>
              <th className="px-3 py-2.5 text-right">Data room</th>
            </tr>
          </thead>
          <tbody>
            {data.per_org.filter((o) => o.mandates_total || o.ndas_signed || o.shares).map((o) => (
              <tr key={o.org_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`dvum-org-${o.org_id}`}>
                <td className="px-3 py-2.5 text-zinc-200 font-medium">{o.org_name}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[#EFD127]">{chf(o.volume_chf)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[#1D9E75]">{chf(o.commission_chf)}</td>
                <td className="px-3 py-2.5 text-right text-zinc-300">{o.mandates_active}/{o.mandates_total}</td>
                <td className="px-3 py-2.5 text-right text-zinc-300">{o.ndas_signed}</td>
                <td className="px-3 py-2.5 text-right text-zinc-300">{o.shares}</td>
                <td className="px-3 py-2.5 text-right text-zinc-300">{o.dataroom_docs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-600">Généré le {(data.generated_at || "").slice(0, 16).replace("T", " ")} · Étage 2 (facturation à la transaction) non activé — les événements facturables montrent le potentiel de take rate.</div>
    </div>
  );
}
